import React, { createElement, useEffect, useMemo, useState } from "react";
import { visuallyHidden } from "@mui/utils";
import {
  Box,
  Checkbox,
  Chip,
  GridLegacy,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import L, { LatLng } from "leaflet";
import { MaterialIcon } from "ui-widgets";
import { PointStyling } from "models/explorer/point-styling";
import BoundaryOrPoint from "models/explorer/boundary-or-point";
import { ExplorerTableProps } from "models/explorer/explorer-table-props";
import { useNavigate } from "react-router-dom";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: {
    [key in Key]:
      | string
      | number
      | string[]
      | boolean
      | any[]
      | PointStyling
      | null
      | undefined
      | JSON;
  },
  b: {
    [key in Key]:
      | string
      | string[]
      | boolean
      | number
      | any[]
      | PointStyling
      | null
      | undefined
      | JSON;
  },
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
// function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
//   const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
//   stabilizedThis.sort((a, b) => {
//     const order = comparator(a[0], b[0]);
//     if (order !== 0) {
//       return order;
//     }
//     return a[1] - b[1];
//   });
//   return stabilizedThis.map((el) => el[0]);
// }

interface HeadCell {
  disablePadding: boolean;
  id: keyof BoundaryOrPoint;
  label: string;
  sort: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "name",
    disablePadding: true,
    label: "Name",
    sort: true,
  },
  {
    id: "type",
    disablePadding: false,
    label: "Point or boundary",
    sort: true,
  },
  {
    id: "description",
    disablePadding: false,
    label: "Description",
    sort: true,
  },
  {
    id: "tags",
    disablePadding: false,
    label: "Tags",
    sort: true,
  },
  {
    id: "guid",
    disablePadding: false,
    label: "Action",
    sort: false,
  },
];

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof BoundaryOrPoint,
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler =
    (property: keyof BoundaryOrPoint) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all points and boundaries",
            }}
            title="select all points and boundaries checkbox"
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={"left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const ResultsTable = ({
  pointsAndBoundaries,
  map,
  sendDisplayList,
}: ExplorerTableProps) => {
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof BoundaryOrPoint>("name");
  const [selected, setSelected] = useState<string[]>(
    pointsAndBoundaries.map((row) => row.guid),
  );
  const [rowsOfPointsAndBoundaries, setRowsOfPointsAndBoundaries] =
    useState<readonly BoundaryOrPoint[]>(pointsAndBoundaries);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof BoundaryOrPoint,
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getIcon = (pointOrBoundary: BoundaryOrPoint) => {
    const marker = L.ExtraMarkers.icon({
      markerColor: pointOrBoundary?.styling?.markerColor ?? "blue-dark",
      shape: pointOrBoundary?.styling?.shape ?? "circle",
      prefix: pointOrBoundary?.styling?.prefix ?? "i",
      icon: pointOrBoundary?.styling?.icon ?? "",
    }).createIcon();
    const icon =
      marker?.children[0]?.className &&
      createElement("i", { className: marker.children[0].className });
    return createElement(
      "div",
      {
        className: marker.className,
        style: {
          position: "inherit",
          width: "32px",
          height: "44px",
          objectFit: "cover",
          margin: "auto 0px auto auto",
        },
      },
      icon,
    );
  };

  useEffect(() => {
    setSelected(pointsAndBoundaries.map((row) => row.guid));
    setPage(0);
    setRowsOfPointsAndBoundaries(
      pointsAndBoundaries.filter(
        (row) =>
          (row.name && row.name.toLowerCase().includes(searchValue)) ||
          (row.description &&
            row.description.toLowerCase().includes(searchValue)),
      ),
    );
    // console.log(pointsAndBoundaries)
  }, [pointsAndBoundaries, searchValue]);

  useEffect(() => sendDisplayList(selected), [selected, sendDisplayList]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(
        pointsAndBoundaries.map((pointOrBoundary) => pointOrBoundary.guid),
      );
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, guid: string) => {
    const selectedIndex = selected.indexOf(guid);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, guid);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (guid: string) => selected.indexOf(guid) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - rowsOfPointsAndBoundaries.length)
      : 0;

  const visibleRows = useMemo(
    () =>
      rowsOfPointsAndBoundaries
        .slice()
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [rowsOfPointsAndBoundaries, order, orderBy, page, rowsPerPage],
  );

  return (
    <Box sx={{ width: "100%" }}>
      <TextField
        sx={{ m: 1, pr: 2 }}
        type="search"
        label="Search"
        id="search-textfield"
        fullWidth
        inputProps={{ inputMode: "search" }}
        onChange={(event) => {
          setSearchValue(event.target.value.toLowerCase());
        }}
      />
      <Paper sx={{ width: "100%" }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} size={"small"}>
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={pointsAndBoundaries.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = isSelected(row.guid);
                const labelId = `enhanced-table-checkbox-${index}`;
                // console.log(row.name)

                return (
                  <TableRow
                    hover
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.guid}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        onClick={(event) => handleClick(event, row.guid)}
                        color="primary"
                        checked={isItemSelected}
                        inputProps={{
                          "aria-labelledby": labelId,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                    >
                      <GridLegacy display={"flex"} alignItems={"center"}>
                        <Typography>{row.name}</Typography>
                        {row.type === "point" ? (
                          <>{getIcon(row)}</>
                        ) : (
                          <div
                            style={{
                              margin: "auto 0px auto auto",
                              minWidth: "24px",
                              minHeight: "24px",
                              backgroundColor: row.styling?.colour,
                            }}
                          ></div>
                        )}
                      </GridLegacy>
                    </TableCell>
                    <TableCell>
                      {row.type === "boundary" ? (
                        <Chip
                          color="secondary"
                          label="Boundary"
                          icon={
                            <Icon baseClassName="material-symbols-outlined">
                              pentagon
                            </Icon>
                          }
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          color="secondary"
                          label="Point"
                          icon={
                            <Icon baseClassName="material-symbols-outlined">
                              location_on
                            </Icon>
                          }
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>
                      {row.tags &&
                        row.tags.length > 0 &&
                        row.tags.map((tag) => (
                          <Chip
                            key={`tag-${tag}-${row.guid}`}
                            sx={{ mr: 1, mb: 1 }}
                            color="primary"
                            label={tag}
                            variant="filled"
                          />
                        ))}
                    </TableCell>
                    <TableCell>
                      {row.input_id && row.input_id.length > 0 ? (
                        <Tooltip title="Show inputs">
                          <IconButton
                            onClick={() =>
                              navigate(
                                `/search?${row.type === "boundary" ? "boundaries" : "points"}=${row.name}`,
                              )
                            }
                          >
                            <MaterialIcon icon="visibility" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <></>
                      )}
                      <Tooltip title="Zoom in">
                        <IconButton
                          onClick={() => {
                            if (row.type === "boundary") {
                              // console.log(row.leafletGeojson)
                              map.flyToBounds(row.leafletGeojson);
                            } else if (row.type === "point") {
                              // console.log(row.latitude, row.longitude)
                              map.flyTo(
                                new LatLng(row.latitude, row.longitude),
                                13,
                              );
                            }
                          }}
                        >
                          <MaterialIcon icon="zoom_in" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 33 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rowsOfPointsAndBoundaries.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default ResultsTable;
